import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Formik, Form, Field } from 'formik';
import * as Yup from 'yup';
import {
  Box,
  Typography,
  TextField,
  Button,
  Grid,
  Paper,
  CircularProgress,
  Alert,
  Divider,
  FormControlLabel,
  Switch
} from '@mui/material';
import { ArrowBack as ArrowBackIcon, Save as SaveIcon } from '@mui/icons-material';
import apiService from '../../services/apiService';
import LocationAutocomplete from '../../components/LocationAutocomplete';

// Esquema de validación
const BranchSchema = Yup.object().shape({
  branch_name: Yup.string().required('El nombre de la sucursal es requerido'),
  location_ubigeo: Yup.number().required('La ubicación es requerida'),
  is_enabled: Yup.boolean()
});

const BranchForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditMode = !!id;

  const [branch, setBranch] = useState(null);
  const [loading, setLoading] = useState(isEditMode);
  const [error, setError] = useState(null);
  const [locations, setLocations] = useState([]);
  const [loadingLocations, setLoadingLocations] = useState(true);

  // Valores iniciales para el formulario
  const initialValues = {
    branch_name: '',
    location_ubigeo: '',
    is_enabled: true
  };

  // Cargar ubicaciones
  useEffect(() => {
    const fetchLocations = async () => {
      try {
        setLoadingLocations(true);
        const data = await apiService.getLocationUbigeos();
        setLocations(data);
      } catch (err) {
        console.error('Error al cargar ubicaciones:', err);
        setError('Error al cargar las ubicaciones. Por favor, intente nuevamente.');
      } finally {
        setLoadingLocations(false);
      }
    };

    fetchLocations();
  }, []);

  // Cargar datos de la sucursal si estamos en modo edición
  useEffect(() => {
    if (isEditMode) {
      const fetchBranch = async () => {
        try {
          setLoading(true);
          const data = await apiService.getBranch(id);
          setBranch(data);
        } catch (err) {
          console.error('Error al cargar sucursal:', err);
          setError('Error al cargar los datos de la sucursal. Por favor, intente nuevamente.');
        } finally {
          setLoading(false);
        }
      };

      fetchBranch();
    }
  }, [id, isEditMode]);

  const handleSubmit = async (values, { setSubmitting }) => {
    try {
      if (isEditMode) {
        await apiService.updateBranch(id, values);
      } else {
        await apiService.createBranch(values);
      }
      navigate('/branches');
    } catch (err) {
      console.error('Error al guardar sucursal:', err);
      setError('Error al guardar los datos de la sucursal. Por favor, intente nuevamente.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleCancel = () => {
    navigate('/branches');
  };

  if (loading || loadingLocations) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="60vh">
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" alignItems="center" mb={3}>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={handleCancel}
          sx={{ mr: 2 }}
        >
          Volver
        </Button>
        <Typography variant="h4">
          {isEditMode ? 'Editar Sucursal' : 'Nueva Sucursal'}
        </Typography>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      <Paper sx={{ p: 3 }}>
        <Formik
          initialValues={branch || initialValues}
          validationSchema={BranchSchema}
          onSubmit={handleSubmit}
          enableReinitialize
        >
          {({ errors, touched, isSubmitting, values, handleChange, setFieldValue }) => (
            <Form>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Información de la Sucursal
                  </Typography>
                  <Divider sx={{ mb: 2 }} />
                </Grid>

                <Grid item xs={12}>
                  <Field
                    as={TextField}
                    name="branch_name"
                    label="Nombre de la Sucursal"
                    fullWidth
                    variant="outlined"
                    error={touched.branch_name && Boolean(errors.branch_name)}
                    helperText={touched.branch_name && errors.branch_name}
                  />
                </Grid>

                <Grid item xs={12}>
                  <LocationAutocomplete
                    locations={locations}
                    value={values.location_ubigeo}
                    onChange={(value) => setFieldValue('location_ubigeo', value)}
                    error={touched.location_ubigeo && Boolean(errors.location_ubigeo)}
                    helperText={touched.location_ubigeo && errors.location_ubigeo}
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Field
                        as={Switch}
                        name="is_enabled"
                        color="primary"
                        checked={values.is_enabled}
                      />
                    }
                    label="Sucursal Activa"
                  />
                </Grid>

                <Grid item xs={12} sx={{ mt: 2 }}>
                  <Box display="flex" justifyContent="flex-end">
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      sx={{ mr: 1 }}
                    >
                      Cancelar
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      color="primary"
                      disabled={isSubmitting}
                      startIcon={isSubmitting ? <CircularProgress size={20} /> : <SaveIcon />}
                    >
                      {isSubmitting ? 'Guardando...' : 'Guardar'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </Form>
          )}
        </Formik>
      </Paper>
    </Box>
  );
};

export default BranchForm; 